import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ManageRolePermissions from './ManageRolePermissions';
import styles from '../../ManagementPages.module.css';
import '../add-edit-role/RoleManagement.css';
import useAxios from '../../../services/AxiosConfig';
import { LeftArrowIcon } from '../../../components/icons/Icons';
import { showAlert } from '../../../components/PerinHealthAlertSlice';

const AddRoleMain = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const accountId = useSelector((state) => state.userInfo.id);
    const {organizations, groups, isAdmin, hasInpatientGroup} = useSelector((state) => state.userInfo);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);


    const updatingRole = location.state && location.state.editRoleData;
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedRoleType, setSelectedRoleType] = useState(0);
    const [roleName, setRoleName] = useState('');
    const [roleDescription, setRoleDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState('');


    const inpatientOrg = useMemo(() => {
        if (isAdmin) {
            if (selectedOrganization) {
                const matchingOrg = groups.find(organization => organization.organizationId === Number(selectedOrganization));
                if (matchingOrg && matchingOrg.groups) {
                    return matchingOrg.groups.some(group => group.inpatient);
                }
            }
            return false;
        } else {
            return hasInpatientGroup;
        }
    }, [selectedOrganization]);

    const allRoleTypes = [
        { id: 1, name: 'Admin' },
        { id: 2, name: 'Staff' },
        { id: 3, name: 'Patient' },
        { id: 4, name: 'Doctor' },
        { id: 5, name: 'Nurse' },
        { id: 6, name: 'Tester' }
    ];

    const roleTypes = useMemo(() => {
        if (!isAdmin) {
            return allRoleTypes.filter(role => role.id !== 6);
        }
        return allRoleTypes;
    }, [isAdmin]);

    

    useEffect(() => {
        if (updatingRole) {
            fetchRoleData(location.state.editRoleData.id);
        }
    }, [updatingRole, location.state]);

    const fetchRoleData = async (roleId) => {
        try {
            const response = await httpService.get(`role/${roleId}`);
            const { name, description, category, organizationId, permissions } = response.data.data; // Access data using response.data.data
            setRoleName(name || '');
            setRoleDescription(description || '');
            function getRoleId(category) {
                switch (category) {
                    case 'Admin':
                        return 1;
                    case 'Staff':
                        return 2;
                    case 'Patient':
                        return 3;
                    case 'Doctor':
                        return 4;
                    case 'Nurse':
                        return 5;
                    case 'Tester':
                        return 6;
                    default:
                        return null; // or a default value if there's no match
                }
            }

            setSelectedRoleType(getRoleId(category));            
            setSelectedOrganization(organizationId || '');
            setSelectedPermissions(Array.isArray(permissions) ? permissions.map(p => p.permissionId) : []);
        } catch (error) {
            console.error('Error fetching role data:', error);
            setErrorMessage('Failed to fetch role data');
        }
    };



    const onSaveHandler = () => {
        if (!selectedRoleType || !roleName.trim() || (isAdmin && !selectedOrganization)) {
            setErrorMessage('Please fill out all required fields.');
            return;
        }

        if (!isAdmin && !organizations[0]){
            setErrorMessage('Error setting user organizations.');
            return;
        }

        const roleTypeNames = {
            1: 'Admin',
            2: 'Staff',
            3: 'Patient',
            4: 'Doctor',
            5: 'Nurse',
            6: 'Tester'
        };

        const roleData = {
            organizationId: isAdmin ? selectedOrganization : organizations[0].id,
            name: roleName,
            description: roleDescription,
            category: roleTypeNames[selectedRoleType] || 'Unknown', // Default to 'Unknown' if no match is found
            createdBy: {
                accountId: accountId
            },
            permissions: selectedPermissions.map(permission => ({ permissionId: permission }))
        };

        if (updatingRole) {
            updateRole(roleData, location.state.editRoleData.id);
        } else {
            createRole(roleData);
        }
    };

    const createRole = async (roleData) => {
        try {
            const response = await httpService.post('role', roleData);
            console.log('Role created successfully:', response.data);
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Role created successfully",
                    type: 'success',
                })
            );
            navigate('/role-management');
        } catch (error) {
            console.error('Error creating role:', error);
            let errorMessage = "Failed to create role";
            if (error.response?.data?.message) {
                errorMessage = error.response?.data?.message;
            }
            setErrorMessage(errorMessage);
            dispatch(
                showAlert({
                    header: "Error",
                    message: errorMessage,
                    type: 'danger'
                })
            );
        }
    };

    const updateRole = async (roleData, roleId) => {
        try {
            const response = await httpService.put(`role/${roleId}`, roleData);
            console.log('Role updated successfully:', response.data);
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Role updated successfully",
                    type: 'success',
                })
            );
            navigate('/role-management');
        } catch (error) {
            console.error('Error updating role:', error);
            setErrorMessage('Failed to update role');
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Failed to update role`,
                    type: 'danger'
                })
            );
        }
    };

    const getRoleTypeName = (roleTypeId) => {
        const role = allRoleTypes.find(role => role.id === roleTypeId);
        return role ? role.name.toLowerCase() : '';
    };

    return (
        <div className={styles.MainCard}>
            <div className={styles.ContentTitle}>{updatingRole ? "Update Role" : "Add Role"}</div>
            <div className={styles.HideScrollContainer} >
                <span hidden={errorMessage === ""} style={{ color: "red", fontSize: "12px" }}>{errorMessage}</span>
                <div style={{display:"flex", flexDirection:"column"}}>
                {isAdmin && (
                    <select
                        className="role-input"
                        value={selectedOrganization}
                        onChange={(e) => setSelectedOrganization(e.target.value)}
                        style={{ width: "300px" }}
                        disabled={updatingRole}
                    >
                        <option value="">Select an Organization</option>
                        {organizations.map((organization) => (
                            <option key={organization.id} value={organization.id}>
                                {organization.name}
                            </option>
                        ))}
                    </select>
                )}
                <select
                    className="role-input"
                    disabled={updatingRole}
                    style={{ width: "300px" }}
                    value={selectedRoleType}
                    onChange={(e) => setSelectedRoleType(Number(e.target.value))}
                >
                    {selectedRoleType === 0 && <option value={0}>Select a Role Type</option>}
                    {roleTypes.map((roleType) => (
                        <option key={roleType.id} value={roleType.id}>{roleType.name}</option>
                    ))}
                </select>
                {selectedRoleType !== 0 && (
                    <>
                        <input
                            type="text"
                            className="role-input"
                            style={{ width: "300px" }}
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            placeholder="Enter role name"
                        />
                        <textarea
                            className="role-input"
                            style={{ width: "300px", height: "100px" }}
                            value={roleDescription}
                            onChange={(e) => setRoleDescription(e.target.value)}
                            placeholder="Enter role description"
                        />
                        <div style={{ paddingBottom: "10px" }}>
                            <ManageRolePermissions
                                roleType={getRoleTypeName(selectedRoleType)}
                                initialPermissions={selectedPermissions}
                                updatePermissions={setSelectedPermissions}
                                inpatientOrg={inpatientOrg}
                            />
                        </div>
                    </>
                )}
                </div>
            </div>
            <div className={styles.ButtonsFooterContainer}>
                <button className={`${styles.CreateButton} ${!selectedRoleType || !roleName.trim() || (isAdmin && !selectedOrganization) ? 'localDisabledButton' : ''}`}
                    onClick={onSaveHandler}
                    disabled={!selectedRoleType || !roleName.trim() || (isAdmin && !selectedOrganization)}>
                    {updatingRole ? "Update" : "Add"}
                </button>
                <button className={styles.CancelButton} onClick={() => navigate('/role-management')}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

export default AddRoleMain;
